import React, { useMemo } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import { openCreateCustomMetricPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useDispatch } from 'react-redux';

import { TRACKING_OPTIONS_MAP } from 'constants/ProjectConstants';

import InputLabel from 'uiToolkitV1/InputLabel';
import { SelectV2 } from 'uiToolkitV1/SelectV2';
import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography';

import { QueryDetails } from './TextInputComponents';
import { useLinkPluginMetricMutation, usePluginTrackingMetricsQuery } from './datahooks';

export const TRACKING_QUERY_TYPES = {
  CUSTOM: 'CUSTOM',
  CUSTOM_PERCENTAGE: 'CUSTOM_PERCENTAGE',
};

const TrackingMetrics = ({ onChange, isReadOnly, metricsList, selectedMetric }) => {
  const labelKey = 'name';
  const theme = useTheme();
  return (
    <SelectV2
      isReadOnly={isReadOnly}
      menuItems={metricsList}
      labelKey={labelKey}
      valueKey="id"
      selectedItem={selectedMetric}
      renderControlItem={(item) => {
        if (!item) {
          return <TextBodyMedium color="text.g2">Select a tracking metric</TextBodyMedium>;
        }
        return <TextBodyMedium>{item?.[labelKey]} </TextBodyMedium>;
      }}
      renderMenuItem={(item) => (
        <Box>
          <TextBodyMedium>{item?.[labelKey]}</TextBodyMedium>
          <TextLabelSmall color={`${theme.colors.text.g2} !important`}>
            {item?.isPredefined ? 'Predefined' : (item?.subText ?? 'Custom')}
          </TextLabelSmall>
        </Box>
      )}
      onSelect={(metric) => {
        onChange(metric);
      }}
    />
  );
};

const AutomaticTrackingMetrics = ({ okrId, pluginType, onChange, isReadOnly }) => {
  const dispatch = useDispatch();
  const { data: pluginTrackingMetric, isInitialLoading } = usePluginTrackingMetricsQuery({
    okrId,
    pluginType,
  });

  const metricsList = useMemo(() => {
    const createNewOptions =
      pluginType === TRACKING_OPTIONS_MAP.DARWINBOX_PLUGIN.value
        ? []
        : [
            {
              id: TRACKING_QUERY_TYPES.CUSTOM,
              name: 'Custom Metric',
              subText: 'Create a custom metric',
            },
            {
              id: TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE,
              name: 'Custom Percentage Metric',
              subText: 'Create a custom % metric',
            },
          ];

    return Array.isArray(pluginTrackingMetric?.metrics) ? [...pluginTrackingMetric?.metrics, ...createNewOptions] : [];
  }, [pluginTrackingMetric?.metrics, pluginType]);

  const selectedMetric = metricsList?.find((metric) => metric?.id === pluginTrackingMetric?.selectedMetricId);

  const linkPluginMetricMutation = useLinkPluginMetricMutation({
    okrId,
    pluginType,
  });

  const handleExistingMetricSelection = (pluginMetric) => {
    const isPredefined = () => {
      const selected = metricsList.filter((m) => m.value === pluginMetric.selectedMetricId)[0];
      return Boolean(selected && selected.isPredefined && !selected.isCustomQuery);
    };

    const { isCustomQuery, id: metricId, query, ratioQuery, queryType } = pluginMetric;

    // const isCustomQuery = pluginMetric.isCustomQuery;
    // const metricId = isCustomQuery ? null : pluginMetric.id;
    // const name = isPredefined() ? null : pluginMetric.name;
    // const query = isCustomQuery ? pluginMetric.query : null;

    // const ratioQuery =
    //   pluginMetric.selectedMetricId === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE
    //     ? pluginMetric.ratioQuery
    //     : null;

    // const queryType = [
    //   TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE,
    //   TRACKING_QUERY_TYPES.CUSTOM
    // ].includes(pluginMetric.selectedMetricId)
    //   ? pluginMetric.selectedMetricId
    //   : null;

    linkPluginMetricMutation.mutate({
      metricId,
      // filters,
      // name: null,
      // isCustomQuery,
      // query: `assignee IN ('aman2@mesh.ai')`,
      // ratioQuery,
      // queryType
    });
  };

  const handlePredefinedMetricSelection = (metric) => {
    dispatch(
      openCreateCustomMetricPanel({
        pluginMetric: pluginTrackingMetric,
        isPredefined: true,
        query: metric?.query,
      })
    );
  };

  const handleTrackingMetricSelection = (metric) => {
    const isCreateCustomMetric = [TRACKING_QUERY_TYPES.CUSTOM, TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE].includes(
      metric?.id
    );

    const isPredefinedCustomMetric = metric?.isPredefined;

    if (isPredefinedCustomMetric) {
      handlePredefinedMetricSelection(metric);
      return;
    }
    if (!isCreateCustomMetric) {
      handleExistingMetricSelection(metric);
      return;
    }
    // handle create new workflow.
    dispatch(
      openCreateCustomMetricPanel({
        pluginMetric: pluginTrackingMetric,
        queryType: metric?.id,
      })
    );
  };

  return (
    <>
      <InputLabel labelText="Please select how do you want to track progress">
        {isInitialLoading ? (
          <>Loading...</>
        ) : (
          <TrackingMetrics
            onChange={handleTrackingMetricSelection}
            isReadOnly={isReadOnly}
            okrId={okrId}
            pluginType={pluginType}
            metricsList={metricsList}
            selectedMetric={selectedMetric}
          />
        )}
      </InputLabel>
      {selectedMetric ? <QueryDetails queryMetric={selectedMetric} isDisabled={isReadOnly} isReadOnly /> : null}
    </>
  );
};

export default AutomaticTrackingMetrics;
